import React from 'react';
import { FiArrowLeft, FiCamera, FiTrash2, FiCheck, FiEye, FiX, FiStar } from 'react-icons/fi';
import { MdOutlineSell, MdOutlineDrafts, MdOutlinePending } from 'react-icons/md';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Button from '../../../components/common/Button';
import Badge from '../../../components/common/Badge';
import { IMAGE_BASE_URL } from '../../../services/api';

const DesktopEditListing = ({
  listing,
  form,
  specializedData,
  categories,
  existingImages,
  newImages,
  error,
  submitting,
  categoryType,
  conditionOptions,
  conditionConfig,
  isListingVisibleToBuyers,
  onBack,
  onSubmit,
  onFieldChange,
  onSpecializedDataChange,
  onImageSelect,
  onRemoveExistingImage,
  onRemoveNewImage,
  onQuickStatusUpdate,
  onDeleteListing,
  renderSpecializedForm
}) => {
  const totalImages = existingImages.length + newImages.length;

  const statusConfig = {
    active: { label: 'Active', color: 'text-green-800', bgColor: 'bg-green-100', icon: FiEye },
    sold: { label: 'Sold', color: 'text-red-800', bgColor: 'bg-red-100', icon: MdOutlineSell },
    draft: { label: 'Draft', color: 'text-gray-800', bgColor: 'bg-gray-100', icon: MdOutlineDrafts },
    pending: { label: 'Pending', color: 'text-yellow-800', bgColor: 'bg-yellow-100', icon: MdOutlinePending },
  };

  const currentStatus = statusConfig[form.status] || statusConfig.active;
  const StatusIcon = currentStatus.icon;

  // Handle status button clicks - directly call the parent handler which uses the hook
  const handleStatusClick = (status) => {
    if (status === 'active' && form.status !== 'active') {
      onQuickStatusUpdate('active');
    } else if (status === 'sold' && form.status !== 'sold') {
      onQuickStatusUpdate('sold');
    } else if (status === 'draft' && form.status !== 'draft') {
      onQuickStatusUpdate('draft');
    }
  };

  return (
    <div className="hidden lg:block min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
            disabled={submitting}
          >
            <FiArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Listing</h1>
            <p className="text-sm text-gray-500">ID: {listing.id} • {listing.title}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Visibility Status */}
            {isListingVisibleToBuyers ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <div className="flex items-center gap-3">
                  <FiCheck className="w-6 h-6 text-green-600" />
                  <div>
                    <h3 className="text-lg font-semibold text-green-700">Visible to buyers</h3>
                    <p className="text-green-600 text-sm">
                      This listing is approved and active in the marketplace
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <div className="flex items-center gap-3">
                  <FiEye className="w-6 h-6 text-yellow-600" />
                  <div>
                    <h3 className="text-lg font-semibold text-yellow-700">
                      {listing.moderation_status !== 'approved' ? 'Awaiting Approval' : 'Not Active'}
                    </h3>
                    <p className="text-yellow-600 text-sm">
                      {listing.moderation_status !== 'approved' 
                        ? 'Your listing is pending admin approval and is not visible to buyers'
                        : 'Your listing is approved but not currently active'
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Status Overview */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-green-50 rounded-xl">
                  <StatusIcon className="w-7 h-7 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Current Status</h2>
                  <p className="text-gray-600 text-sm">
                    {form.status === 'active' && 'Your listing is visible to buyers'}
                    {form.status === 'sold' && 'This item has been sold'}
                    {form.status === 'draft' && 'Your listing is saved but not published'}
                    {form.status === 'pending' && 'Your listing is under review'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center justify-between mb-6">
                <Badge type="status" status={form.status} size="large" />
                <div className="text-right">
                  <p className="text-sm text-gray-500">Moderation Status</p>
                  <Badge 
                    type="moderation" 
                    status={listing.moderation_status} 
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Quick Status Actions - All buttons call the same handler */}
              <div className="grid grid-cols-3 gap-3">
                {/* Active Button - Only show if not already active */}
                {form.status !== 'active' && (
                  <button
                    onClick={() => handleStatusClick('active')}
                    className="flex flex-col items-center p-3 border border-green-200 rounded-lg hover:bg-green-50 transition group"
                    disabled={submitting}
                  >
                    <FiEye className="w-5 h-5 text-green-600 mb-2 group-hover:scale-110 transition" />
                    <span className="text-sm font-medium text-gray-700">Make Active</span>
                  </button>
                )}
                
                {/* Sold Button - Only show if not already sold */}
                {form.status !== 'sold' && (
                  <button
                    onClick={() => handleStatusClick('sold')}
                    className="flex flex-col items-center p-3 border border-red-200 rounded-lg hover:bg-red-50 transition group"
                    disabled={submitting}
                  >
                    <MdOutlineSell className="w-5 h-5 text-red-600 mb-2 group-hover:scale-110 transition" />
                    <span className="text-sm font-medium text-gray-700">Mark as Sold</span>
                  </button>
                )}
                
                {/* Draft Button - Only show if not already draft */}
                {form.status !== 'draft' && (
                  <button
                    onClick={() => handleStatusClick('draft')}
                    className="flex flex-col items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition group"
                    disabled={submitting}
                  >
                    <MdOutlineDrafts className="w-5 h-5 text-gray-600 mb-2 group-hover:scale-110 transition" />
                    <span className="text-sm font-medium text-gray-700">Save as Draft</span>
                  </button>
                )}
              </div>
            </div>

            {/* Basic Information */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Basic Information</h3>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    placeholder="Listing title"
                    value={form.title}
                    onChange={(e) => onFieldChange('title', e.target.value)}
                    disabled={submitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    placeholder="Describe your listing in detail..."
                    value={form.description}
                    onChange={(e) => onFieldChange('description', e.target.value)}
                    rows={4}
                    disabled={submitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Price *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500">₦</span>
                      </div>
                      <input
                        type="number"
                        placeholder="0.00"
                        value={form.price}
                        onChange={(e) => onFieldChange('price', e.target.value)}
                        disabled={submitting}
                        className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="flex items-end">
                    <button
                      onClick={() => onFieldChange('is_negotiable', !form.is_negotiable)}
                      disabled={submitting}
                      className={`flex items-center gap-2 p-4 border rounded-lg transition ${
                        form.is_negotiable 
                          ? 'bg-green-50 border-green-500 text-green-700' 
                          : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <span className={`w-5 h-5 flex items-center justify-center rounded-full border ${
                        form.is_negotiable ? 'bg-green-500 border-green-500 text-white' : 'border-gray-400'
                      }`}>
                        {form.is_negotiable && '✓'}
                      </span>
                      <span className="text-sm font-medium">Negotiable</span>
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      value={form.category_id}
                      onChange={(e) => onFieldChange('category_id', e.target.value)}
                      disabled={submitting}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value="">Select category</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Condition
                    </label>
                    <select
                      value={form.condition}
                      onChange={(e) => onFieldChange('condition', e.target.value)}
                      disabled={submitting}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      {conditionOptions.map(option => (
                        <option key={option.id} value={option.value}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Specialized Form */}
            {categoryType && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {categoryType.charAt(0).toUpperCase() + categoryType.slice(1)} Details
                  </h3>
                  <Badge 
                    type={categoryType} 
                    status={categoryType}
                    className="capitalize"
                  />
                </div>
                {renderSpecializedForm()}
              </div>
            )}

            {/* Photos */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Photos</h3>
                <span className="text-sm text-gray-500">
                  {totalImages}/10 photos
                </span>
              </div>

              {/* Existing Images */}
              {existingImages.length > 0 && (
                <div className="mb-8">
                  <h4 className="text-sm font-medium text-gray-700 mb-4">Current Photos</h4>
                  <div className="grid grid-cols-4 gap-4">
                    {existingImages.map((image) => (
                      <div key={image.image_id} className="relative group">
                        <img
                          src={`${IMAGE_BASE_URL}${image.image_url}`}
                          alt="Listing"
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => onRemoveExistingImage(image.image_id)}
                          disabled={submitting}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition opacity-0 group-hover:opacity-100"
                        >
                          <FiX className="w-3 h-3" />
                        </button>
                        {image.is_primary && (
                          <div className="absolute top-2 left-2 bg-blue-500 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
                            <FiStar className="w-3 h-3" />
                            Primary
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add New Images */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-4">Add More Photos</h4>
                <div className="grid grid-cols-4 gap-4">
                  {newImages.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image.uri}
                        alt="New"
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <button
                        onClick={() => onRemoveNewImage(index)}
                        disabled={submitting}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition"
                      >
                        <FiX className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  
                  {totalImages < 10 && (
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={onImageSelect}
                        className="hidden"
                        disabled={submitting}
                      />
                      <div className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:border-orange-400 hover:bg-orange-50 transition">
                        <FiCamera className="w-8 h-8 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">Add Photo</span>
                        <span className="text-xs text-gray-500">{10 - totalImages} remaining</span>
                      </div>
                    </label>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Actions */}
          <div className="space-y-6">
            {/* Update Button */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Update Listing</h3>
              
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <Button
                onClick={onSubmit}
                disabled={submitting}
                loading={submitting}
                variant="primary"
                className="w-full mb-3"
                icon={<FiCheck className="w-5 h-5" />}
              >
                {submitting ? 'Updating...' : 'Update Listing'}
              </Button>
              
              <Button
                onClick={onBack}
                disabled={submitting}
                variant="outline"
                className="w-full"
              >
                Cancel
              </Button>
            </div>

            {/* Delete Listing */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-semibold mb-4 text-red-600">Danger Zone</h3>
              
              <button
                onClick={onDeleteListing}
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 p-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition"
              >
                <FiTrash2 className="w-5 h-5" />
                Delete Listing
              </button>
              
              <p className="text-xs text-gray-500 text-center mt-3">
                This action cannot be undone
              </p>
            </div>

            {/* Listing Stats */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Listing Statistics</h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Views</span>
                  <span className="font-semibold text-gray-900">{listing.view_count || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Photos</span>
                  <span className="font-semibold text-gray-900">{existingImages.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Created</span>
                  <span className="font-semibold text-gray-900">
                    {listing.created_at ? new Date(listing.created_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Last Updated</span>
                  <span className="font-semibold text-gray-900">
                    {listing.updated_at ? new Date(listing.updated_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DesktopEditListing;